import { h } from 'vue';

import { USER_TYPES } from '~/constants';
import { useProfileStore } from '~/store';

import { capitalize } from '~/features/useUtilities';

import UserListPendingUserInfo from '~/components/UserListPendingUserInfo.vue';
import UserListUserDetails from '~/components/UserListUserDetails.vue';
import UserListUserTypeTag from '~/components/UserListUserTypeTag.vue';

import type {
  IAdminUserListUsersTableRow,
  IProfileType,
  UserListUsersTableRow,
} from '~/types';

export const useUserDataTable = () => {
  const profileStore = useProfileStore();

  const userColumns = {
    details: {
      header: 'User Details',
      key: 'details',
      visible: true,
      render: (item?: IAdminUserListUsersTableRow) => {
        return item?.email_verified
          ? h(UserListUserDetails, {
              firstName: item?.first_name,
              lastName: item?.last_name,
            })
          : UserListPendingUserInfo;
      },
    },
    organisations: {
      header: 'Organisation',
      key: 'organisations',
      visible: true,
      transform: (item: IAdminUserListUsersTableRow) => {
        const isInternal = item?.user_type === USER_TYPES.G2O;

        if (isInternal) {
          return null;
        }

        if (item.has_all_organisations) return 'All Organisations';
        const organisationsLength = item.organisations.length;

        if (!organisationsLength) return 'No Organisations';

        const [organisation] = item.organisations;

        return organisationsLength > 1
          ? `${organisation} (+${organisationsLength - 1})`
          : organisation;
      },
    },
    email: {
      header: 'User Email',
      key: 'email',
    },
    user_type: {
      header: 'User Type',
      key: 'user_type',
      render: () => UserListUserTypeTag,
    },
    role: {
      header: 'User Role',
      key: 'role',
      valueFormatter: (value: string) => capitalize(value),
    },
    business_type: {
      header: `Company's Business`,
      key: 'business_type',
    },
  };

  const getUserState = (role?: any, user_type?: IProfileType) => {
    const isInternal = user_type === USER_TYPES.G2O;
    const isPublic = user_type === USER_TYPES.PUBLIC;
    const isCustomer = user_type === USER_TYPES.CUSTOMER;
    const isInternalAdmin = isInternal && role === 'admin';

    return {
      isInternal,
      isPublic,
      isCustomer,
      isInternalAdmin,
    };
  };

  const getAdminActionsState = (targetUser?: IAdminUserListUsersTableRow) => {
    const isCurrentUser = profileStore.profile?.id === targetUser?.id;
    const isSuperUser = profileStore.isSuper;

    const targetUserState = getUserState(
      targetUser?.role,
      targetUser?.user_type,
    );

    const actionButtons = {
      edit: { tooltip: 'Edit', disabled: false },
      remove: { tooltip: 'Remove', disabled: false },
    };

    if (isCurrentUser) {
      actionButtons.edit.tooltip = 'This action is not possible.';
      actionButtons.remove.tooltip = 'This action is not possible.';
    }

    if (targetUserState.isPublic) {
      actionButtons.edit.tooltip = 'Public users cannot be edited.';
    }

    if (targetUserState.isCustomer) {
      actionButtons.edit.tooltip =
        'Please select assigned User’s organisation on the left side panel in order to edit this User.';
    }

    if (isSuperUser && targetUserState.isInternalAdmin) {
      actionButtons.edit.tooltip = 'G2O admin cannot be edited.';
      actionButtons.remove.tooltip = 'G2O admin cannot be removed.';
    }

    actionButtons.edit.disabled = actionButtons.edit.tooltip !== 'Edit';
    actionButtons.remove.disabled = actionButtons.remove.tooltip !== 'Remove';

    return {
      actionButtons,
      isCurrentUser,
      isPublicUser: targetUserState.isPublic,
    };
  };

  const getOrganisationActionsState = (targetUser?: UserListUsersTableRow) => {
    const isCurrentUser = profileStore.profile?.id === targetUser?.id;

    const targetUserState = getUserState(
      targetUser?.role,
      targetUser?.user_type,
    );

    const actionButtons = {
      edit: { tooltip: 'Edit', disabled: false },
      remove: { tooltip: 'Remove', disabled: false },
    };

    if (isCurrentUser) {
      actionButtons.edit.tooltip = 'This action is not possible.';
      actionButtons.remove.tooltip = 'This action is not possible.';
    }

    if (targetUserState.isInternal) {
      actionButtons.edit.tooltip = 'G2O users can be edited on the User List.';
    }

    if (targetUserState.isInternalAdmin) {
      actionButtons.edit.tooltip = 'G2O admin cannot be edited.';
      actionButtons.remove.tooltip = 'G2O admin cannot be removed.';
    }

    actionButtons.edit.disabled = actionButtons.edit.tooltip !== 'Edit';
    actionButtons.remove.disabled = actionButtons.remove.tooltip !== 'Remove';

    return {
      actionButtons,
      isCurrentUser,
      isPublicUser: targetUserState.isPublic,
    };
  };

  return {
    userColumns,
    getOrganisationActionsState,
    getAdminActionsState,
    getUserState,
  };
};
